import { useEffect, useRef, useState } from "react";
import { ImageIcon, X, Loader2 } from "lucide-react";
import { resolveAssetUrl } from "@/lib/assetUrl";
import { cn } from "@/lib/utils";

export type DefaultKind = "bg" | "fpv_flower" | "fpv_ring" | "fpv_dinner";

interface DefaultImagePickerProps {
  /** The kind of defaults to pick from. */
  kind: DefaultKind;
  /** Currently selected value (may be absolute URL or relative path). */
  value: string;
  /** Called with the relative path (e.g. "bg/default_1.jpg"). */
  onPick: (relativePath: string) => void;
  /** Called when the user wants to clear the selection entirely. */
  onClear?: () => void;
  label?: string;
  disabled?: boolean;
}

interface ManifestResponse {
  success: boolean;
  baseUrl: string;
  kinds: Record<DefaultKind, string[]>;
}

const KIND_LABELS: Record<DefaultKind, string> = {
  bg: "Background",
  fpv_flower: "FPV — Flowers",
  fpv_ring: "FPV — Ring",
  fpv_dinner: "FPV — Dinner",
};

export function DefaultImagePicker({
  kind,
  value,
  onPick,
  onClear,
  label,
  disabled = false,
}: DefaultImagePickerProps) {
  const [open, setOpen] = useState(false);
  const [files, setFiles] = useState<string[]>([]);
  const [baseUrl, setBaseUrl] = useState<string>("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const dialogRef = useRef<HTMLDivElement | null>(null);

  // Resolve the currently selected value back to its relative path for highlight.
  const currentRelative = isRelativePath(value) ? value : "";

  // Close on Escape.
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [open]);

  // Load manifest when opened.
  useEffect(() => {
    if (!open) return;
    setLoading(true);
    setError(null);
    fetch("/api/static-defaults-manifest")
      .then((r) => r.json() as Promise<ManifestResponse>)
      .then((data) => {
        if (!data.success) throw new Error("Manifest returned failure");
        setFiles(data.kinds[kind] ?? []);
        setBaseUrl(data.baseUrl);
      })
      .catch((e) => {
        setError(e instanceof Error ? e.message : "Failed to load defaults");
      })
      .finally(() => setLoading(false));
  }, [open, kind]);

  // Click outside the dialog panel closes it.
  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (dialogRef.current && !dialogRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        disabled={disabled}
        className="inline-flex items-center gap-1.5 rounded-lg border border-zinc-200 bg-white px-3 py-1.5 text-xs font-medium text-zinc-700 hover:bg-zinc-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-zinc-700 dark:bg-zinc-800 dark:text-zinc-300 dark:hover:bg-zinc-700"
      >
        <ImageIcon className="h-3.5 w-3.5" />
        Browse {label ?? KIND_LABELS[kind]}
      </button>

      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div
            ref={dialogRef}
            className="flex max-h-[80vh] w-full max-w-3xl flex-col rounded-xl bg-white shadow-xl dark:bg-zinc-900"
          >
            <div className="flex items-center justify-between border-b border-zinc-200 p-4 dark:border-zinc-800">
              <div>
                <h3 className="text-lg font-bold text-zinc-900 dark:text-zinc-50">
                  Pick a default {KIND_LABELS[kind]}
                </h3>
                <p className="text-xs text-zinc-500 dark:text-zinc-400">
                  Stored as a relative path (e.g. <code>{kind}/default_1.jpg</code>). The
                  server resolves it to an absolute URL on read.
                </p>
              </div>
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="rounded-lg p-1 hover:bg-zinc-100 dark:hover:bg-zinc-800"
                aria-label="Close"
              >
                <X className="h-5 w-5 text-zinc-500" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-4">
              {loading && (
                <div className="flex items-center justify-center py-12 text-zinc-500">
                  <Loader2 className="h-5 w-5 animate-spin" />
                </div>
              )}
              {error && (
                <div className="rounded-lg bg-red-50 p-3 text-sm text-red-600 dark:bg-red-900/20 dark:text-red-400">
                  {error}
                </div>
              )}
              {!loading && !error && files.length === 0 && (
                <div className="py-12 text-center text-sm text-zinc-500 dark:text-zinc-400">
                  No defaults available for this kind.
                </div>
              )}
              {!loading && !error && files.length > 0 && (
                <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4">
                  {files.map((file) => {
                    const rel = `${kind}/${file}`;
                    const isSelected = currentRelative === rel;
                    return (
                      <button
                        key={file}
                        type="button"
                        onClick={() => {
                          onPick(rel);
                          setOpen(false);
                        }}
                        className={cn(
                          "group relative overflow-hidden rounded-lg border-2 transition-all",
                          isSelected
                            ? "border-merry-pink ring-2 ring-merry-pink/30"
                            : "border-zinc-200 hover:border-zinc-300 dark:border-zinc-700 dark:hover:border-zinc-600",
                        )}
                      >
                        <div className="aspect-square w-full bg-zinc-100 dark:bg-zinc-800">
                          <img
                            src={resolveAssetUrl(rel)}
                            alt={file}
                            loading="lazy"
                            className="h-full w-full object-cover"
                          />
                        </div>
                        <div className="absolute bottom-0 left-0 right-0 bg-black/60 px-2 py-1 text-[10px] font-medium text-white">
                          {file}
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            <div className="flex items-center justify-end gap-2 border-t border-zinc-200 p-4 dark:border-zinc-800">
              {onClear && value && (
                <button
                  type="button"
                  onClick={() => {
                    onClear();
                    setOpen(false);
                  }}
                  className="rounded-lg px-3 py-1.5 text-xs font-medium text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20"
                >
                  Clear selection
                </button>
              )}
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="rounded-lg border border-zinc-200 px-3 py-1.5 text-xs font-medium text-zinc-700 hover:bg-zinc-50 dark:border-zinc-700 dark:text-zinc-300 dark:hover:bg-zinc-800"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Render baseUrl hint for debugging — visible in dev only */}
      {baseUrl && import.meta.env.DEV && (
        <span className="ml-2 text-[10px] text-zinc-400">
          ({baseUrl}/)
        </span>
      )}
    </>
  );
}

function isRelativePath(v: string): boolean {
  return /^(bg|fpv_flower|fpv_ring|fpv_dinner)\//.test(v);
}